import { DeleteIcon, Grid, IconButton, TableCell, TableRow } from '@cmg/design-system';
import {
  FormikCurrencyField,
  FormikPercentageField,
  FormikSelectField,
} from '@cmg/design-system-formik';
import { useFormikContext } from 'formik';
import defaultTo from 'lodash/defaultTo';
import React, { useCallback, useMemo } from 'react';

import {
  getDefaultLimitTypeForOffering,
  getLimitTypesForOffering,
} from '../../../../../../../../../common/util/offering/offeringTypeHelpers';
import {
  InstitutionalIndicationOrderType,
  InterestLevelLimitType,
  OfferingType,
} from '../../../../../../../../../graphql';
import { limitTypeLabels } from '../../../../../../../../../types/domain/order-book/constants';
import { institutionalIndicationOrderTypeLabels } from '../../../../../../../../order-book/constants';
import { IndicationFormValues, InterestLevelValue } from '../../utils/IndicationSectionValidation';
import { FormikInterestQuantityField } from './FormikInterestQuantityField';
import {
  getIsLimitTypeFieldHidden,
  getOrderTypeUnitFieldProps,
  updateInterestLevelsOrderType,
} from './InterestLevelFormRow.model';

export type Props = Readonly<{
  index: number;
  onRemove: () => void;
  rowValue: InterestLevelValue;
  offeringType: OfferingType;
  isLimitIndicationsOnlyEnabled: boolean;
  demandQuantityAdornmentOverride: string | null;
}>;

const InterestLevelFormRow: React.FC<Props> = ({
  onRemove,
  rowValue,
  index,
  offeringType,
  isLimitIndicationsOnlyEnabled,
  demandQuantityAdornmentOverride,
}) => {
  const { isSubmitting, setFieldValue, values } = useFormikContext<IndicationFormValues>();

  const { limitType, interestUnit, orderType } = rowValue;

  const limitTypeOptions = useMemo(() => getLimitTypesForOffering(offeringType), [offeringType]);

  const isLimitTypeCurrency = limitType === InterestLevelLimitType.Price || limitType === null;
  const isOrderTypeLimit = orderType === InstitutionalIndicationOrderType.Limit;

  const { hidden: isOrderTypeFieldHidden, options: orderTypeFieldOptions } = useMemo(() => {
    return getOrderTypeUnitFieldProps({
      isLimitIndicationsOnlyEnabled,
      interestLevels: values.interestLevels,
      currentOrderType: orderType,
    });
  }, [isLimitIndicationsOnlyEnabled, orderType, values.interestLevels]);

  const isLimitTypeFieldHidden = useMemo(
    () => getIsLimitTypeFieldHidden(values.interestLevels, index),
    [index, values.interestLevels]
  );

  const defaultLimitType = useMemo(() => {
    return (
      values.interestLevels.find(({ limitType }) => !!limitType)?.limitType ??
      getDefaultLimitTypeForOffering(offeringType)
    );
  }, [offeringType, values.interestLevels]);

  const handleOrderTypeChange = useCallback(
    (value: unknown) => {
      // without using generic select component the value is unknown, but setting generics does not work on nested schema
      const newValue = value as InstitutionalIndicationOrderType | null;

      if (newValue === orderType) {
        return;
      }

      const nextInterestLevels = updateInterestLevelsOrderType({
        currentRowIndex: index,
        interestLevels: values.interestLevels,
        newOrderType: newValue,
        defaultLimitType,
      });

      setFieldValue('interestLevels', nextInterestLevels);
    },
    [defaultLimitType, index, orderType, setFieldValue, values.interestLevels]
  );

  const handleLimitTypeChange = useCallback(
    (value: unknown) => {
      // without using generic select component the value is unknown, but setting generics does not work on nested schema
      const newValue = value as InterestLevelLimitType | null;

      if (newValue === limitType) {
        return;
      }

      // when changing limit type we set this limit type to every interest level that has limit order type and reset limit amount
      const nextInterestLevels = values.interestLevels.map<InterestLevelValue>(level => {
        return {
          ...level,
          limitPrice: null,
          limitType:
            level.orderType === InstitutionalIndicationOrderType.Limit ? newValue : level.limitType,
        };
      });

      setFieldValue('interestLevels', nextInterestLevels);
    },
    [limitType, setFieldValue, values.interestLevels]
  );

  return (
    <TableRow>
      <TableCell>
        <FormikInterestQuantityField
          aria-label="interestQuantity"
          name={`interestLevels.${index}.interestQuantity`}
          disabled={isSubmitting}
          fullWidth
          showHelperTextInTooltip
          currencyCode={values.currencyCode}
          interestUnit={interestUnit}
          endAdornmentOverride={demandQuantityAdornmentOverride}
        />
      </TableCell>
      <TableCell>
        {isOrderTypeFieldHidden ? (
          institutionalIndicationOrderTypeLabels[orderType!]
        ) : (
          <FormikSelectField
            aria-label="orderType"
            name={`interestLevels.${index}.orderType`}
            disabled={isSubmitting}
            options={orderTypeFieldOptions}
            onChange={handleOrderTypeChange}
            showHelperTextInTooltip
            fullWidth
          />
        )}
      </TableCell>
      <TableCell>
        {isOrderTypeLimit && (
          <Grid container alignItems="center" spacing={1}>
            <Grid item xs={6}>
              {isLimitTypeFieldHidden ? (
                limitTypeLabels[limitType!]
              ) : (
                <FormikSelectField
                  aria-label="limitType"
                  name={`interestLevels.${index}.limitType`}
                  disabled={isSubmitting}
                  options={limitTypeOptions}
                  onChange={handleLimitTypeChange}
                  showHelperTextInTooltip
                  fullWidth
                />
              )}
            </Grid>
            <Grid item xs={6}>
              {isLimitTypeCurrency ? (
                <FormikCurrencyField
                  aria-label="limitPrice"
                  data-testid="currency-limit-type-input-field"
                  name={`interestLevels.${index}.limitPrice`}
                  disabled={isSubmitting}
                  fullWidth
                  showHelperTextInTooltip
                  currencyCode={defaultTo(values.currencyCode, undefined)}
                />
              ) : (
                <FormikPercentageField
                  aria-label="limitPrice"
                  data-testid="percentage-limit-type-input-field"
                  name={`interestLevels.${index}.limitPrice`}
                  disabled={isSubmitting}
                  fullWidth
                  showHelperTextInTooltip
                />
              )}
            </Grid>
          </Grid>
        )}
      </TableCell>
      <TableCell>
        <IconButton title="Remove Interest Level row" color="primary" onClick={onRemove}>
          <DeleteIcon />
        </IconButton>
      </TableCell>
    </TableRow>
  );
};

export default InterestLevelFormRow;
